import { Link, useLocation, Navigate } from "react-router-dom";
import { LogOut, LayoutDashboard, ShoppingBag, Mail, MessageSquare, Tag, Globe, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, exact: true },
  { to: "/admin/orders", label: "Orders", Icon: ShoppingBag },
  { to: "/admin/leads", label: "Email list", Icon: Mail },
  { to: "/admin/messages", label: "Messages", Icon: MessageSquare },
  { to: "/admin/promos", label: "Promo codes", Icon: Tag },
  { to: "/admin/geo", label: "Geography", Icon: Globe },
];

export default function AdminLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#D92D20]" />
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <span className="font-display font-black tracking-tighter text-xl leading-none">
              12<span className="text-[#D92D20]">/</span>LAWS
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] border-l border-white/10 pl-3">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="hidden sm:block text-xs text-[#A3A3A3] hover:text-white">View site ↗</Link>
            <div className="hidden sm:flex items-center gap-2 text-sm text-[#A3A3A3]">
              {user.picture && (
                <img src={user.picture} alt="" className="w-7 h-7 object-cover" />
              )}
              <span className="font-mono-plex text-xs">{user.email}</span>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#D92D20] hover:text-[#D92D20]"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto grid grid-cols-12 gap-0">
        {/* Side nav */}
        <aside className="col-span-12 lg:col-span-2 border-r border-black/10 bg-white">
          <nav className="p-3 lg:p-5 flex lg:flex-col gap-1 overflow-x-auto" data-testid="admin-nav">
            {NAV.map(({ to, label, Icon, exact }) => {
              const active = exact ? location.pathname === to : location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`inline-flex items-center gap-3 px-3 py-2.5 text-sm whitespace-nowrap transition-colors ${active ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A] hover:bg-[#F5F5F5]"}`}
                  data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="col-span-12 lg:col-span-10 px-6 sm:px-10 py-10 min-h-[80vh]">
          {children}
        </main>
      </div>
    </div>
  );
}
